import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

// slice 02.1 · paso b.3 — presentacional. El popover de recordatorio de un comentario de tarea
// (estaba DUPLICADO como JSX en 2 sitios: por-comentario y el global del composer, + el helper
// `renderReminderPopoverDays`). Portalizado a `document.body`. El contenedor mantiene el estado
// (`reminderSelectedUsers`/`reminderSelectedDate`/`reminderMonth`/`reminderYear`/query/focus) y
// `onSave`.
export const ReminderPopover = ({
    onClose, pos, systemUsers = [], selectedUsers = [], onToggleUser,
    searchQuery, onSearchChange, focusedIdx, onFocusedIdxChange,
    month, year, onPrevMonth, onNextMonth, selectedDate, onDaySelect, onSave, error,
}) => {
    const { t: translate } = useTranslation('tasks');
    const filteredOptions = systemUsers
        .filter(u => u.nick.toLowerCase().includes(searchQuery.toLowerCase()))
        .slice(0, 4);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            onFocusedIdxChange(prev => (filteredOptions.length > 0 ? (prev + 1) % filteredOptions.length : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            onFocusedIdxChange(prev => (filteredOptions.length > 0 ? (prev - 1 + filteredOptions.length) % filteredOptions.length : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredOptions.length > 0 && focusedIdx >= 0 && focusedIdx < filteredOptions.length) {
                onToggleUser(filteredOptions[focusedIdx].nick);
            }
        }
    };

    const dayCells = [];
    const firstDayIdx = (new Date(year, month, 1).getDay() + 6) % 7; // lunes = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let i = 0; i < firstDayIdx; i++) {
        dayCells.push(<div key={`empty-${i}`} className="popover-calendar-day empty" />);
    }
    for (let i = 1; i <= daysInMonth; i++) {
        const cellDate = new Date(year, month, i);
        const isSelected = selectedDate
            && selectedDate.getDate() === i
            && selectedDate.getMonth() === month
            && selectedDate.getFullYear() === year;
        dayCells.push(
            <div
                key={`day-${i}`}
                className={`popover-calendar-day ${isSelected ? 'selected' : ''}`}
                onClick={() => onDaySelect(cellDate)}
            >
                {i}
            </div>
        );
    }

    return createPortal(
        <>
            <div className="reminder-transparent-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999998 }} onClick={onClose} />
            <div className="comment-reminder-popover" style={{ position: 'fixed', bottom: pos.bottom, right: pos.right, width: '400px', zIndex: 999999, boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '1px solid rgba(255, 255, 255, 0.08)', backgroundColor: '#1a1a1a', color: '#fff', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)' }} onClick={(e) => e.stopPropagation()}>
                <div className="calendly-popover-columns">
                    {/* Columna Izquierda: Users Tagger */}
                    <div className="calendly-left-col user-tagger-col" style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className="user-select-search-container" style={{ margin: 0, padding: 'var(--space-2) var(--space-3)' }}>
                            <Search size={11} className="search-icon" />
                            <input
                                type="text"
                                aria-label={translate('comments.searchUserAria')}
                                value={searchQuery}
                                onChange={(e) => {
                                    onSearchChange(e.target.value);
                                    onFocusedIdxChange(0);
                                }}
                                onKeyDown={handleKeyDown}
                                placeholder={translate('comments.searchUserPlaceholder')}
                                className="user-select-search-input"
                            />
                        </div>
                        <div className="user-select-options-list" style={{ flex: 1, maxHeight: '200px', overflowY: 'auto' }}>
                            {filteredOptions.map((u, idx) => {
                                const isSelected = selectedUsers.some(su => su.id === u.id);
                                const isFocused = focusedIdx === idx;
                                return (
                                    <div
                                        key={u.id}
                                        className={`user-select-option-item ${isFocused ? 'focused' : ''}`}
                                        onClick={() => onToggleUser(u.nick)}
                                        onMouseEnter={() => onFocusedIdxChange(idx)}
                                    >
                                        <img
                                            src={(u['link-profile-img-thumb'] || u['link-profile-img']) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.nick}`}
                                            alt={u.nick}
                                            className="user-option-avatar"
                                            loading="lazy"
                                        />
                                        <span className="user-option-name">{u.nick}</span>
                                        <div className={`user-option-toggle ${isSelected ? 'active' : ''}`}>
                                            <div className="toggle-handle" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {error && (
                            <div role="alert" style={{ fontSize: '10px', color: '#f87171', padding: '0 var(--space-1) var(--space-1)' }}>
                                {error}
                            </div>
                        )}
                        <button type="button" className="calendly-save-btn" style={{ marginTop: 'auto' }} onClick={onSave}>
                            {translate('comments.save')}
                        </button>
                    </div>

                    {/* Columna Derecha: Calendario */}
                    <div className="calendly-right-col">
                        <div className="calendly-month-nav">
                            <button type="button" className="month-nav-arrow" onClick={onPrevMonth} aria-label={translate('modal.monthPrev')}>&lt;</button>
                            <span className="month-nav-title">{translate('modal.monthNamesShort', { returnObjects: true })[month]} {year}</span>
                            <button type="button" className="month-nav-arrow" onClick={onNextMonth} aria-label={translate('modal.monthNext')}>&gt;</button>
                        </div>
                        <div className="calendly-days-grid-header">
                            {translate('modal.weekDaysShort', { returnObjects: true }).map((d, i) => <span key={i}>{d}</span>)}
                        </div>
                        <div className="calendly-days-grid">
                            {dayCells}
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default ReminderPopover;
